import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import api from "@/api";
import { useAuth, can } from "@/auth";
import { useI18n } from "@/i18n";
import { useCurrency } from "@/currency";
import { useShowPrices } from "@/showPrices";
import { Tile, Loading, StatusPill } from "@/components/Bits";
import { CommentsThread } from "@/components/Extras";
import TranslatableText from "@/components/TranslatableText";
import {
  ArrowLeft, PencilSimple, Trash, Star, ShieldCheck, LinkedinLogo, EnvelopeSimple,
  Phone, DeviceMobile, MapPin, Building, Briefcase, Translate, CalendarBlank,
  Tag, ChatCircleDots, Target, ClockClockwise, WhatsappLogo, PlusCircle, Sparkle, Copy, X, PaperPlaneTilt,
} from "@phosphor-icons/react";const PREF_OPTIONS = [
  { v: "email",     fr: "Email",        en: "Email",        Icon: EnvelopeSimple, color: "#0D71DD" },
  { v: "mobile",    fr: "Mobile",       en: "Mobile",       Icon: DeviceMobile,   color: "#34A853" },
  { v: "phone",     fr: "Téléphone",    en: "Phone",        Icon: Phone,          color: "#F59E0B" },
  { v: "whatsapp",  fr: "WhatsApp",     en: "WhatsApp",     Icon: WhatsappLogo,   color: "#25D366" },
  { v: "linkedin",  fr: "LinkedIn",     en: "LinkedIn",     Icon: LinkedinLogo,   color: "#0A66C2" },
];

const SUGGESTED_TAGS_FR = ["Champion", "Bloqueur", "Influenceur", "Technique", "Achats", "Direction", "Maintenance"];
const SUGGESTED_TAGS_EN = ["Champion", "Blocker", "Influencer", "Technical", "Procurement", "C-level", "Maintenance"];

function daysSince(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  return Math.floor((Date.now() - d.getTime()) / (24 * 3600 * 1000));
}

export default function ContactDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { lang } = useI18n();
  const { user } = useAuth();
  const { fmt: fmtMoney } = useCurrency();
  const { showPrices } = useShowPrices();
  const editable = can(user, "sales");

  const [contact, setContact] = useState(null);
  const [related, setRelated] = useState(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(null);
  const [aiModal, setAiModal] = useState(false);

  const L = (fr, en) => (lang === "fr" ? fr : en);

  const load = async () => {
    try {
      const [d, r] = await Promise.all([
        api.get(`/contacts/${id}`),
        api.get(`/contacts/${id}/related`),
      ]);
      setContact(d.data);
      setRelated(r.data);
      setDraft({ ...d.data, tags: d.data.tags || [] });
    } catch (e) {
      toast.error(L("Contact introuvable", "Contact not found"));
      nav("/contacts");
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const save = async () => {
    try {
      const body = { ...draft, tags: draft.tags || [] };
      delete body.id; delete body.country; delete body.created_at; delete body.created_by;
      delete body.updated_at; delete body.updated_by;
      const r = await api.put(`/contacts/${id}`, body);
      setContact(r.data);
      setDraft({ ...r.data, tags: r.data.tags || [] });
      setEditing(false);
      toast.success(L("Contact mis à jour", "Contact updated"));
    } catch (e) {
      toast.error(e.response?.data?.detail || L("Échec", "Failed"));
    }
  };

  const del = async () => {
    if (!window.confirm(L("Supprimer ce contact ?", "Delete this contact?"))) return;
    try {
      await api.delete(`/contacts/${id}`);
      toast.success(L("Contact supprimé", "Contact deleted"));
      nav("/contacts");
    } catch (e) {
      toast.error(L("Échec", "Failed"));
    }
  };

  const initials = useMemo(() => {
    if (!contact) return "";
    return `${(contact.first_name || "?").charAt(0)}${(contact.last_name || "").charAt(0)}`.toUpperCase();
  }, [contact]);

  if (!contact || !related) return <Loading />;

  const lastVisitDays = daysSince(related.last_visit_date);
  const lastCommentDays = daysSince(related.last_comment_date);
  const lastTouch = [lastVisitDays, lastCommentDays].filter((x) => x != null).sort((a, b) => a - b)[0];
  const stale = lastTouch != null && lastTouch > 90;

  const c = editing ? draft : contact;
  const operatorPlanLink = c.operator
    ? `/account-plans?name=${encodeURIComponent(c.operator)}`
    : null;

  return (
    <div data-testid={`contact-detail-${id}`}>
      {/* ============ Header ============ */}
      <div className="border-b" style={{ borderColor: "var(--border)", background: "var(--bg)" }}>
        <div className="p-4 md:p-8">
          <button
            onClick={() => nav("/contacts")}
            className="text-[12px] flex items-center gap-1.5 mb-4 hover:underline"
            style={{ color: "var(--text-tertiary)" }}
            data-testid="contact-back"
          >
            <ArrowLeft size={12} /> {L("Retour aux contacts", "Back to contacts")}
          </button>

          <div className="flex items-start justify-between flex-wrap gap-4">
            <div className="flex items-start gap-4 flex-1 min-w-0">
              {/* Avatar */}
              <div
                className="w-16 h-16 rounded-[14px] shrink-0 flex items-center justify-center text-[22px] font-bold"
                style={{
                  background: "var(--bg-subtle)",
                  color: "var(--text-secondary)",
                  border: "1px solid var(--border)",
                }}
                data-testid="contact-avatar"
              >
                {initials}
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <h1 className="text-[28px] font-semibold tracking-tight leading-none" style={{ letterSpacing: "-0.025em" }} data-testid="contact-name">
                    {c.first_name} {c.last_name}
                  </h1>
                  {c.decision_maker && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10.5px] font-mono uppercase tracking-wide"
                          style={{ background: "rgba(237,32,39,0.10)", color: "var(--accent)", border: "1px solid var(--accent)" }}>
                      <ShieldCheck size={10} weight="fill" /> {L("Décideur", "Decision Maker")}
                    </span>
                  )}
                  {stale && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10.5px] font-mono uppercase tracking-wide"
                          style={{ background: "rgba(245,158,11,0.10)", color: "#F59E0B", border: "1px solid #F59E0B" }}
                          title={L(`Pas de contact depuis ${lastTouch} jours`, `Silent for ${lastTouch} days`)}>
                      <ClockClockwise size={10} weight="fill" /> {L(`Silencieux ${lastTouch}j`, `Silent ${lastTouch}d`)}
                    </span>
                  )}
                </div>
                <div className="text-[14px] mt-1" style={{ color: "var(--text-secondary)" }}>
                  {c.title || L("(sans titre)", "(no title)")}
                </div>
                <div className="flex items-center gap-3 mt-2 text-[12.5px] flex-wrap" style={{ color: "var(--text-tertiary)" }}>
                  {c.operator && (
                    <span className="flex items-center gap-1">
                      <Building size={12} />
                      {operatorPlanLink ? (
                        <Link to={operatorPlanLink} className="hover:underline" style={{ color: "var(--text-secondary)" }}>{c.operator}</Link>
                      ) : c.operator}
                    </span>
                  )}
                  {c.department && (<><span>·</span><span className="flex items-center gap-1"><Briefcase size={12} /> {c.department}</span></>)}
                  {c.city && (<><span>·</span><span className="flex items-center gap-1"><MapPin size={12} /> {c.city}</span></>)}
                  {c.language && (<><span>·</span><span className="flex items-center gap-1"><Translate size={12} /> {c.language}</span></>)}
                </div>
                <div className="mt-3 flex items-center gap-0.5">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <Star key={i} size={14} weight={i <= (c.relationship_score || 0) ? "fill" : "regular"}
                          style={{ color: i <= (c.relationship_score || 0) ? "#F59E0B" : "var(--border)" }} />
                  ))}
                  <span className="ml-2 text-[11px] font-mono" style={{ color: "var(--text-tertiary)" }}>
                    {L("Score relation", "Relationship")}
                  </span>
                </div>
              </div>
            </div>
            {editable && (
              <div className="flex items-center gap-2">
                {!editing ? (
                  <>
                    {contact.email && (
                      <button onClick={() => setAiModal(true)} className="btn-ghost flex items-center gap-1.5"
                              data-testid="ai-email-btn" title={L("Brouillon email IA", "AI email draft")}>
                        <Sparkle size={13} weight="fill" style={{ color: "#8B5CF6" }} />
                        {L("Brouillon IA", "AI draft")}
                      </button>
                    )}
                    <button onClick={() => setEditing(true)} className="btn-ghost flex items-center gap-1.5" data-testid="contact-edit-btn">
                      <PencilSimple size={13} /> {L("Modifier", "Edit")}
                    </button>
                    <button onClick={del} className="btn-ghost flex items-center gap-1.5" data-testid="contact-delete-btn"
                            style={{ color: "var(--accent)" }}>
                      <Trash size={13} /> {L("Supprimer", "Delete")}
                    </button>
                  </>
                ) : (
                  <>
                    <button onClick={() => { setDraft({ ...contact, tags: contact.tags || [] }); setEditing(false); }} className="btn-ghost">
                      {L("Annuler", "Cancel")}
                    </button>
                    <button onClick={save} className="btn-accent" data-testid="contact-save-btn">
                      {L("Enregistrer", "Save")}
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ============ Body ============ */}
      <div className="p-4 md:p-8 grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
        {/* --- LEFT COLUMN --- */}
        <div className="lg:col-span-2 space-y-4 md:space-y-6">
          <CoordinatesCard c={c} editing={editing} setDraft={setDraft} L={L} />
          <TagsCard c={c} editing={editing} setDraft={setDraft} L={L} lang={lang} />
          <PreferenceCard c={c} editing={editing} setDraft={setDraft} L={L} />
          <LinkedInAddressCard c={c} editing={editing} setDraft={setDraft} L={L} />
          <PublicNotesCard c={c} editing={editing} setDraft={setDraft} L={L} />

          {related.visits.length > 0 && (
            <Tile testId="contact-visits">
              <div className="flex items-center gap-2 mb-3">
                <CalendarBlank size={14} weight="fill" style={{ color: "var(--accent)" }} />
                <h2 className="text-[14px] font-semibold">{L("Historique des visites", "Visits history")} ({related.visits.length})</h2>
              </div>
              <div className="overflow-x-auto">
                <table className="tech w-full">
                  <thead>
                    <tr><th>{L("Date", "Date")}</th><th>{L("Type", "Type")}</th><th>{L("Lieu", "Location")}</th><th>{L("Notes", "Notes")}</th></tr>
                  </thead>
                  <tbody>
                    {related.visits.slice(0, 10).map((v, i) => (
                      <tr key={i}>
                        <td className="font-mono text-[11px]">{v.date}</td>
                        <td>{v.type || "—"}</td>
                        <td className="text-[12px]">{v.operator || v.city || "—"}</td>
                        <td className="text-[12px]" style={{ color: "var(--text-secondary)" }}>{(v.notes || "").slice(0, 80)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Tile>
          )}

          {related.opportunities.length > 0 && (
            <Tile testId="contact-opps">
              <div className="flex items-center gap-2 mb-3">
                <Target size={14} weight="fill" style={{ color: "var(--accent)" }} />
                <h2 className="text-[14px] font-semibold">{L("Opportunités liées", "Related opportunities")} (top 5)</h2>
              </div>
              <div className="overflow-x-auto">
                <table className="tech w-full">
                  <thead>
                    <tr><th>ID</th><th>{L("Sous-système", "Sub-system")}</th><th>{L("Plateforme", "Platform")}</th><th>{L("OVH", "OVH")}</th><th>{L("Statut", "Status")}</th>{showPrices && <th className="text-right">{L("Valeur active", "Active value")}</th>}</tr>
                  </thead>
                  <tbody>
                    {related.opportunities.map((o) => (
                      <tr key={o.id}>
                        <td className="font-mono text-[11px]">{o.id}</td>
                        <td className="text-[12px]">{o.sub_system}</td>
                        <td className="text-[12px]" style={{ color: "var(--text-secondary)" }}>{o.platform}</td>
                        <td className="font-mono text-[11px]">{o.next_ovh || "—"}</td>
                        <td><StatusPill status={o.ovh_status} /></td>
                        {showPrices && <td className="num font-medium text-wabtec-red">{fmtMoney(o.active_method || 0)}</td>}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Tile>
          )}
        </div>

        {/* --- RIGHT COLUMN --- */}
        <div className="space-y-4 md:space-y-6">
          {/* Audit trail */}
          <Tile testId="contact-audit">
            <div className="flex items-center gap-2 mb-3">
              <ClockClockwise size={14} weight="fill" style={{ color: "var(--text-tertiary)" }} />
              <h2 className="text-[14px] font-semibold">{L("Traçabilité", "Audit trail")}</h2>
            </div>
            <dl className="space-y-2 text-[12px]">
              <AuditRow label={L("Créé par", "Created by")} value={contact.created_by} />
              <AuditRow label={L("Le", "Date")} value={contact.created_at ? new Date(contact.created_at).toLocaleString() : "—"} />
              <AuditRow label={L("Modifié par", "Updated by")} value={contact.updated_by} />
              <AuditRow label={L("Le", "Date")} value={contact.updated_at ? new Date(contact.updated_at).toLocaleString() : "—"} />
              {related.last_visit_date && <AuditRow label={L("Dernière visite", "Last visit")} value={`${related.last_visit_date} (${lastVisitDays}j)`} />}
              {related.last_comment_date && <AuditRow label={L("Dernière note", "Last note")} value={`${new Date(related.last_comment_date).toLocaleDateString()} (${lastCommentDays}j)`} />}
            </dl>
          </Tile>

          {/* Notes thread */}
          <Tile testId="contact-notes-thread" className="flex flex-col" style={{ minHeight: 500 }}>
            <div className="flex items-center gap-2 mb-3">
              <ChatCircleDots size={14} weight="fill" style={{ color: "var(--accent)" }} />
              <h2 className="text-[14px] font-semibold">{L("Notes internes", "Internal notes")}</h2>
            </div>
            <div className="flex-1 min-h-[400px]">
              <CommentsThread entityType="contact" entityId={id} embedded />
            </div>
          </Tile>
        </div>
      </div>

      {aiModal && <AIEmailModal contactId={id} contact={contact} lang={lang} onClose={() => setAiModal(false)} L={L} />}
    </div>
  );
}

function AuditRow({ label, value }) {
  return (
    <div className="flex justify-between gap-3">
      <dt className="font-mono text-[11px] uppercase tracking-wide shrink-0" style={{ color: "var(--text-tertiary)" }}>{label}</dt>
      <dd className="text-right truncate" style={{ color: "var(--text-primary)" }}>{value || "—"}</dd>
    </div>
  );
}

function FieldText({ label, value, editing, onChange, testId, type = "text", placeholder = "" }) {
  if (!editing) {
    return (
      <div>
        <div className="text-[10.5px] font-mono uppercase tracking-wide mb-0.5" style={{ color: "var(--text-tertiary)" }}>{label}</div>
        <div className="text-[13.5px]" style={{ color: "var(--text-primary)" }}>{value || "—"}</div>
      </div>
    );
  }
  return (
    <label className="block">
      <div className="text-[10.5px] font-mono uppercase tracking-wide mb-1" style={{ color: "var(--text-tertiary)" }}>{label}</div>
      <input
        type={type}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testId}
        className="w-full px-3 py-1.5 text-[13.5px] rounded-[8px]"
        style={{ background: "var(--bg-elevated)", border: "1px solid var(--border)", color: "var(--text-primary)" }}
      />
    </label>
  );
}

function CoordinatesCard({ c, editing, setDraft, L }) {
  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
  return (
    <Tile testId="contact-coords">
      <div className="flex items-center gap-2 mb-3">
        <EnvelopeSimple size={14} weight="fill" style={{ color: "var(--accent)" }} />
        <h2 className="text-[14px] font-semibold">{L("Coordonnées", "Contact details")}</h2>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {!editing ? (
          <>
            <LinkRow Icon={EnvelopeSimple} label={L("Email", "Email")}     value={c.email}  href={c.email ? `mailto:${c.email}` : null} />
            <LinkRow Icon={DeviceMobile}   label={L("Mobile", "Mobile")}   value={c.mobile} href={c.mobile ? `tel:${String(c.mobile).replace(/\s+/g, "")}` : null} />
            <LinkRow Icon={Phone}          label={L("Téléphone", "Phone")} value={c.phone}  href={c.phone ? `tel:${String(c.phone).replace(/\s+/g, "")}` : null} />
            <LinkRow Icon={WhatsappLogo}   label="WhatsApp" value={c.mobile} href={c.mobile ? `https://wa.me/${String(c.mobile).replace(/\D/g, "")}` : null} target="_blank" />
          </>
        ) : (
          <>
            <FieldText label="Email"      value={c.email}  editing onChange={(v) => set("email", v)}  testId="edit-email" type="email" />
            <FieldText label="Mobile"     value={c.mobile} editing onChange={(v) => set("mobile", v)} testId="edit-mobile" />
            <FieldText label={L("Téléphone", "Phone")}   value={c.phone}  editing onChange={(v) => set("phone", v)}  testId="edit-phone" />
            <FieldText label={L("Ville", "City")}        value={c.city}   editing onChange={(v) => set("city", v)}   testId="edit-city" />
            <FieldText label={L("Département", "Department")} value={c.department} editing onChange={(v) => set("department", v)} testId="edit-department" />
            <FieldText label={L("Titre", "Title")}       value={c.title}  editing onChange={(v) => set("title", v)}  testId="edit-title" />
            <FieldText label={L("Opérateur", "Operator")} value={c.operator} editing onChange={(v) => set("operator", v)} testId="edit-operator" />
            <FieldText label={L("Langue", "Language")}   value={c.language} editing onChange={(v) => set("language", v)} testId="edit-language" />
            <div className="md:col-span-2 flex items-center gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={!!c.decision_maker} onChange={(e) => set("decision_maker", e.target.checked)}
                       className="accent-[var(--accent)] w-4 h-4" data-testid="edit-decision-maker" />
                <span className="text-[12.5px]">{L("Décideur", "Decision Maker")}</span>
              </label>
              <label className="flex items-center gap-2">
                <span className="text-[10.5px] font-mono uppercase tracking-wide" style={{ color: "var(--text-tertiary)" }}>{L("Score", "Score")}</span>
                <select value={c.relationship_score || 3} onChange={(e) => set("relationship_score", parseInt(e.target.value))}
                        className="px-2 py-1 text-[13px] rounded-[6px]" data-testid="edit-score"
                        style={{ background: "var(--bg-elevated)", border: "1px solid var(--border)", color: "var(--text-primary)" }}>
                  {[1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>{n}</option>)}
                </select>
              </label>
            </div>
          </>
        )}
      </div>
    </Tile>
  );
}

function LinkRow({ Icon, label, value, href, target }) {
  if (!value) {
    return (
      <div>
        <div className="text-[10.5px] font-mono uppercase tracking-wide mb-0.5" style={{ color: "var(--text-tertiary)" }}>{label}</div>
        <div className="text-[13px]" style={{ color: "var(--text-tertiary)" }}>—</div>
      </div>
    );
  }
  return (
    <div>
      <div className="text-[10.5px] font-mono uppercase tracking-wide mb-0.5 flex items-center gap-1" style={{ color: "var(--text-tertiary)" }}>
        <Icon size={11} /> {label}
      </div>
      {href ? (
        <a href={href} target={target} rel={target === "_blank" ? "noopener noreferrer" : undefined}
           className="text-[13.5px] font-mono hover:underline" style={{ color: "var(--text-primary)" }}>
          {value}
        </a>
      ) : (
        <div className="text-[13.5px]">{value}</div>
      )}
    </div>
  );
}

function TagsCard({ c, editing, setDraft, L, lang }) {
  const tags = c.tags || [];
  const suggested = lang === "fr" ? SUGGESTED_TAGS_FR : SUGGESTED_TAGS_EN;
  const [newTag, setNewTag] = useState("");
  const addTag = (t) => {
    const v = t.trim();
    if (!v || tags.includes(v)) return;
    setDraft((d) => ({ ...d, tags: [...(d.tags || []), v] }));
    setNewTag("");
  };
  const remove = (t) => setDraft((d) => ({ ...d, tags: (d.tags || []).filter((x) => x !== t) }));
  return (
    <Tile testId="contact-tags">
      <div className="flex items-center gap-2 mb-3">
        <Tag size={14} weight="fill" style={{ color: "var(--accent)" }} />
        <h2 className="text-[14px] font-semibold">{L("Étiquettes", "Tags")}</h2>
      </div>
      <div className="flex flex-wrap gap-2">
        {tags.length === 0 && !editing && (
          <span className="text-[12px] italic" style={{ color: "var(--text-tertiary)" }}>{L("Aucune étiquette", "No tags")}</span>
        )}
        {tags.map((t) => (
          <span key={t} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11.5px] font-mono"
                style={{ background: "rgba(13,113,221,0.10)", color: "#0D71DD", border: "1px solid #0D71DD" }}>
            {t}
            {editing && (
              <button onClick={() => remove(t)} className="ml-0.5 hover:opacity-70" data-testid={`tag-remove-${t}`}>×</button>
            )}
          </span>
        ))}
      </div>
      {editing && (
        <>
          <div className="flex items-center gap-2 mt-3">
            <input
              value={newTag}
              onChange={(e) => setNewTag(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addTag(newTag))}
              placeholder={L("Nouvelle étiquette…", "New tag…")}
              data-testid="tag-input"
              className="px-2 py-1 text-[12.5px] rounded-[8px] flex-1"
              style={{ background: "var(--bg-elevated)", border: "1px solid var(--border)", color: "var(--text-primary)" }}
            />
            <button onClick={() => addTag(newTag)} className="btn-ghost text-[12px] flex items-center gap-1" data-testid="tag-add-btn">
              <PlusCircle size={13} /> {L("Ajouter", "Add")}
            </button>
          </div>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {suggested.filter((s) => !tags.includes(s)).map((s) => (
              <button key={s} onClick={() => addTag(s)}
                      className="text-[11px] px-2 py-0.5 rounded-full hover:opacity-80"
                      data-testid={`tag-suggest-${s}`}
                      style={{ background: "var(--bg-subtle)", color: "var(--text-tertiary)", border: "1px dashed var(--border)" }}>
                + {s}
              </button>
            ))}
          </div>
        </>
      )}
    </Tile>
  );
}

function PreferenceCard({ c, editing, setDraft, L }) {
  const set = (v) => setDraft((d) => ({ ...d, preferred_contact: v }));
  return (
    <Tile testId="contact-preference">
      <div className="flex items-center gap-2 mb-3">
        <Star size={14} weight="fill" style={{ color: "var(--accent)" }} />
        <h2 className="text-[14px] font-semibold">{L("Préférence de contact", "Preferred channel")}</h2>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        {PREF_OPTIONS.map((p) => {
          const active = c.preferred_contact === p.v;
          const Icon = p.Icon;
          return (
            <button
              key={p.v}
              onClick={() => editing && set(active ? null : p.v)}
              disabled={!editing}
              data-testid={`pref-${p.v}`}
              className="flex flex-col items-center justify-center gap-1 py-2.5 px-2 rounded-[10px] transition-all"
              style={{
                background: active ? p.color : "var(--bg-elevated)",
                color: active ? "white" : "var(--text-secondary)",
                border: `1px solid ${active ? p.color : "var(--border)"}`,
                cursor: editing ? "pointer" : "default",
                opacity: editing || active ? 1 : 0.5,
              }}
            >
              <Icon size={16} weight={active ? "fill" : "regular"} />
              <span className="text-[10.5px] font-mono">{L(p.fr, p.en)}</span>
            </button>
          );
        })}
      </div>
    </Tile>
  );
}

function LinkedInAddressCard({ c, editing, setDraft, L }) {
  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
  return (
    <Tile testId="contact-linkedin">
      <div className="flex items-center gap-2 mb-3">
        <LinkedinLogo size={14} weight="fill" style={{ color: "#0A66C2" }} />
        <h2 className="text-[14px] font-semibold">{L("Présence en ligne & adresse", "Online presence & address")}</h2>
      </div>
      <div className="space-y-4">
        {editing ? (
          <>
            <FieldText label="LinkedIn URL" value={c.linkedin_url} editing onChange={(v) => set("linkedin_url", v)}
                       testId="edit-linkedin" placeholder="https://linkedin.com/in/…" />
            <FieldText label={L("Adresse postale", "Postal address")} value={c.address} editing onChange={(v) => set("address", v)}
                       testId="edit-address" placeholder={L("Rue, code postal, ville", "Street, ZIP, city")} />
          </>
        ) : (
          <>
            <LinkRow Icon={LinkedinLogo} label="LinkedIn" value={c.linkedin_url} href={c.linkedin_url} target="_blank" />
            <LinkRow Icon={MapPin} label={L("Adresse", "Address")} value={c.address} />
          </>
        )}
      </div>
    </Tile>
  );
}

function PublicNotesCard({ c, editing, setDraft, L }) {
  const set = (v) => setDraft((d) => ({ ...d, notes: v }));
  return (
    <Tile testId="contact-public-notes">
      <div className="flex items-center gap-2 mb-3">
        <ChatCircleDots size={14} weight="fill" style={{ color: "var(--accent)" }} />
        <h2 className="text-[14px] font-semibold">{L("Notes publiques", "Public notes")}</h2>
        <span className="text-[10.5px] font-mono" style={{ color: "var(--text-tertiary)" }}>
          {L("(visibles sur les exports)", "(visible in exports)")}
        </span>
      </div>
      {editing ? (
        <textarea
          value={c.notes || ""}
          onChange={(e) => set(e.target.value)}
          rows={4}
          data-testid="edit-notes"
          className="w-full px-3 py-2 text-[13px] rounded-[10px]"
          style={{ background: "var(--bg-elevated)", border: "1px solid var(--border)", color: "var(--text-primary)" }}
        />
      ) : c.notes ? (
        <TranslatableText
          text={c.notes}
          className="text-[13px]"
          style={{ color: "var(--text-primary)" }}
        />
      ) : (
        <p className="text-[13px] italic" style={{ color: "var(--text-tertiary)" }}>
          {L("Aucune note", "No notes")}
        </p>
      )}
    </Tile>
  );
}


const GOAL_OPTIONS = [
  { v: "introduction",        fr: "Premier contact",     en: "First touch" },
  { v: "follow_up",           fr: "Relance",             en: "Follow-up" },
  { v: "propose_meeting",     fr: "Proposer un RDV",     en: "Propose meeting" },
  { v: "commercial_check_in", fr: "Check-in commercial", en: "Commercial check-in" },
];

function AIEmailModal({ contactId, contact, lang, onClose, L }) {
  const [goal, setGoal] = useState("introduction");
  const [emailLang, setEmailLang] = useState(lang || "fr");
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState(null);

  const generate = async () => {
    setBusy(true);
    setDraft(null);
    try {
      const r = await api.post(`/contacts/${contactId}/draft-email`, { goal, lang: emailLang });
      setDraft(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || L("Échec de la génération", "Generation failed"));
    } finally {
      setBusy(false);
    }
  };

  const copyAll = async () => {
    if (!draft) return;
    const txt = `${draft.subject}\n\n${draft.body}`;
    try {
      await navigator.clipboard.writeText(txt);
      toast.success(L("Copié dans le presse-papier", "Copied to clipboard"));
    } catch {
      toast.error(L("Copie impossible", "Copy failed"));
    }
  };

  const openMailto = () => {
    if (!draft) return;
    const to = encodeURIComponent(draft.contact_email || contact.email || "");
    const sub = encodeURIComponent(draft.subject || "");
    const body = encodeURIComponent(draft.body || "");
    window.location.href = `mailto:${to}?subject=${sub}&body=${body}`;
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.5)", backdropFilter: "blur(8px)" }}
      onClick={onClose}
      data-testid="ai-email-modal"
    >
      <div
        className="tile p-7 w-full max-w-2xl fade-in"
        onClick={(e) => e.stopPropagation()}
        style={{ boxShadow: "var(--shadow-lg)", maxHeight: "90vh", overflowY: "auto" }}
      >
        <div className="flex items-start justify-between mb-5">
          <div className="flex items-center gap-2">
            <Sparkle size={18} weight="fill" style={{ color: "#8B5CF6" }} />
            <div>
              <h3 className="text-[20px] font-semibold tracking-tight" style={{ letterSpacing: "-0.02em" }}>
                {L("Brouillon email IA", "AI email draft")}
              </h3>
              <p className="text-[12px] mt-1" style={{ color: "var(--text-tertiary)" }}>
                {L("Pour", "For")} {contact.first_name} {contact.last_name} · {contact.email}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-full hover:bg-[var(--bg-subtle)]" data-testid="ai-modal-close">
            <X size={16} />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <div className="label-tech mb-2">{L("Objectif du message", "Goal")}</div>
            <div className="grid grid-cols-2 gap-2">
              {GOAL_OPTIONS.map((g) => {
                const active = goal === g.v;
                return (
                  <button
                    key={g.v}
                    onClick={() => setGoal(g.v)}
                    data-testid={`ai-goal-${g.v}`}
                    className="px-3 py-2 text-[12.5px] rounded-[8px] transition-all"
                    style={{
                      background: active ? "#8B5CF6" : "var(--bg-elevated)",
                      color: active ? "white" : "var(--text-secondary)",
                      border: `1px solid ${active ? "#8B5CF6" : "var(--border)"}`,
                    }}
                  >
                    {L(g.fr, g.en)}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="label-tech">{L("Langue", "Language")} :</span>
              {["fr", "en", "tr"].map((lc) => {
                const active = emailLang === lc;
                return (
                  <button
                    key={lc}
                    onClick={() => setEmailLang(lc)}
                    data-testid={`ai-lang-${lc}`}
                    className="px-3 py-1 text-[12px] font-mono uppercase rounded-full transition-all"
                    style={{
                      background: active ? "var(--text-primary)" : "transparent",
                      color: active ? "var(--bg)" : "var(--text-secondary)",
                      border: "1px solid var(--border)",
                    }}
                  >
                    {lc}
                  </button>
                );
              })}
            </div>
            <button
              onClick={generate}
              disabled={busy}
              data-testid="ai-generate-btn"
              className="btn-accent flex items-center gap-2"
              style={{ background: "#8B5CF6", borderColor: "#8B5CF6" }}
            >
              <Sparkle size={13} weight="fill" />
              {busy ? L("Génération…", "Generating…") : (draft ? L("Régénérer", "Regenerate") : L("Générer", "Generate"))}
            </button>
          </div>

          {draft && (
            <div className="space-y-3 pt-3" style={{ borderTop: "1px solid var(--border)" }}>
              <div>
                <div className="label-tech mb-1">{L("Sujet", "Subject")}</div>
                <input
                  value={draft.subject}
                  onChange={(e) => setDraft({ ...draft, subject: e.target.value })}
                  data-testid="ai-subject"
                  className="w-full px-3 py-2 text-[14px] font-medium rounded-[8px]"
                  style={{ background: "var(--bg-elevated)", border: "1px solid var(--border)", color: "var(--text-primary)" }}
                />
              </div>
              <div>
                <div className="label-tech mb-1">{L("Corps du message", "Body")}</div>
                <textarea
                  value={draft.body}
                  onChange={(e) => setDraft({ ...draft, body: e.target.value })}
                  rows={10}
                  data-testid="ai-body"
                  className="w-full px-3 py-2 text-[13.5px] rounded-[8px] font-mono leading-relaxed whitespace-pre-wrap"
                  style={{ background: "var(--bg-elevated)", border: "1px solid var(--border)", color: "var(--text-primary)" }}
                />
              </div>
              <div className="flex items-center justify-end gap-2">
                <button onClick={copyAll} className="btn-ghost flex items-center gap-1.5" data-testid="ai-copy-btn">
                  <Copy size={13} /> {L("Copier", "Copy")}
                </button>
                <button onClick={openMailto} className="btn-accent flex items-center gap-1.5" data-testid="ai-mailto-btn">
                  <PaperPlaneTilt size={13} weight="fill" /> {L("Ouvrir dans l'app mail", "Open in mail app")}
                </button>
              </div>
            </div>
          )}

          {!draft && !busy && (
            <p className="text-center py-8 text-[12.5px] italic" style={{ color: "var(--text-tertiary)" }}>
              {L("Choisissez un objectif puis cliquez sur Générer. Claude rédige un message personnalisé en utilisant les notes, l'historique de visites et les opportunités liées à ce contact.",
                 "Pick a goal then click Generate. Claude composes a personalized message using internal notes, visit history and related opportunities for this contact.")}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

